import { useState } from 'react';
import RevealOnScroll from '../base/RevealOnScroll';
import type { FAQ } from '../../mocks/faqs';

interface Props {
  faqs: FAQ[];
  title?: string;
  subtitle?: string;
  bg?: 'white' | 'cream' | 'dark';
}

export default function FAQSection({
  faqs,
  title = 'Frequently Asked Questions',
  subtitle = 'Voice-search and AI-ready answers to what Houston homeowners ask most about our services.',
  bg = 'white',
}: Props) {
  const [open, setOpen] = useState<number | null>(0);

  const bgClass = bg === 'dark' ? 'bg-[#0F0F0F]' : bg === 'cream' ? 'bg-[#F8F4EF]' : 'bg-white';
  const headingColor = bg === 'dark' ? 'text-white' : 'text-[#0F0F0F]';
  const subColor = bg === 'dark' ? 'text-white/45' : 'text-[#7A7265]';
  const borderColor = bg === 'dark' ? 'border-white/10' : 'border-[#E8E2D9]';
  const qColor = bg === 'dark' ? 'text-white/90' : 'text-[#1A1A1A]';
  const aColor = bg === 'dark' ? 'text-white/50' : 'text-[#6B6358]';
  const hoverBg = bg === 'dark' ? 'hover:bg-white/3' : 'hover:bg-[#F8F4EF]';
  const activeBg = bg === 'dark' ? 'bg-[#141210]' : 'bg-[#FDFAF7]';
  const innerBorder = bg === 'dark' ? 'border-white/6' : 'border-[#F0EBE3]';

  return (
    <section className={`${bgClass} py-28 lg:py-36`}>
      <div className="max-w-4xl mx-auto px-6 lg:px-12">

        <RevealOnScroll className="mb-14">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-px bg-[#BF9448]" />
            <span className="text-[#BF9448] text-[10px] font-sans font-semibold tracking-[0.25em] uppercase">
              FAQ
            </span>
          </div>
          <h2
            className={`font-serif font-semibold ${headingColor} leading-tight mb-4`}
            style={{ fontSize: 'clamp(1.9rem, 3vw, 2.75rem)' }}
          >
            {title}
          </h2>
          <p className={`font-sans ${subColor} text-base leading-relaxed`}>{subtitle}</p>
        </RevealOnScroll>

        <div className={`divide-y ${borderColor}`}>
          {faqs.map((faq, i) => (
            <RevealOnScroll key={i} delayMs={i * 40}>
              <div>
                <button
                  onClick={() => setOpen(open === i ? null : i)}
                  className={`w-full flex items-start justify-between gap-6 py-6 text-left ${hoverBg} -mx-4 px-4 transition-colors duration-200 cursor-pointer`}
                >
                  <span className={`font-sans ${qColor} font-medium text-sm leading-relaxed`}>
                    {faq.question}
                  </span>
                  <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i
                      className={`ri-add-line text-[#BF9448] text-xl transition-transform duration-300 ${
                        open === i ? 'rotate-45' : ''
                      }`}
                    ></i>
                  </span>
                </button>
                {open === i && (
                  <div className={`pb-6 -mx-4 px-4 border-t ${innerBorder} pt-5`}>
                    <p className={`font-sans ${aColor} text-sm leading-relaxed`}>
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            </RevealOnScroll>
          ))}
        </div>

      </div>
    </section>
  );
}
